import { ChangeDetectionStrategy, Component, effect, input, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { SafeUrl } from '@angular/platform-browser';

/**
 * Circular avatar. Shows {@link src} when it loads, otherwise falls back to a centered
 * Material icon ({@link fallbackIcon}, default `person`) on a neutral tinted circle.
 *
 * Size is driven by the `--xemex-avatar-size` custom property: set it via the {@link size}
 * input (any CSS length) or from a consumer stylesheet (e.g. with a design token).
 */
@Component({
  selector: 'xemex-avatar',
  standalone: true,
  imports: [MatIconModule],
  templateUrl: './avatar.component.html',
  styleUrl: './avatar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.is-empty]': '!src() || failed()',
    '[style.--xemex-avatar-size]': 'size() || null',
  },
})
export class AvatarComponent {
  /** Image source. When absent or it fails to load, {@link fallbackIcon} is shown instead. */
  src = input<string | SafeUrl | undefined>(undefined);
  /** Any CSS length (e.g. `'3rem'`, `'40px'`). Leave empty to inherit the default / a consumer override. */
  size = input<string>('');
  /** Material icon shown when there is no image. */
  fallbackIcon = input<string>('person');
  alt = input<string>('');

  protected readonly failed = signal(false);

  constructor() {
    // A new source gets a fresh chance to load.
    effect(() => {
      this.src();
      this.failed.set(false);
    });
  }

  protected onError() {
    this.failed.set(true);
  }
}
